import React, { useEffect, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, Pagination, Navigation } from 'swiper/modules'
import { useNavigate } from 'react-router-dom'
import { slugify } from './OffersPage'
import OptimizedImage from '../utils/OptimizedImage'
import { useSalon } from '../contexts/SalonContext'
import { getActiveHeroOffers, clearHeroOfferCache } from '../services/heroOfferService'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

const WELCOME_IMAGE =
  'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1600&h=900&fit=crop'

const slideFrameClass =
  'relative h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] min-h-[400px] max-h-[700px]'

const Hero = ({ onBookNow }) => {
  const navigate = useNavigate()
  const { selectedSalon, salonId, salonReady } = useSalon()
  const salonName = selectedSalon?.name || 'Timeless Trends Salon'
  const [offers, setOffers] = useState([])

  useEffect(() => {
    if (!salonReady || !salonId) return
    let cancelled = false
    clearHeroOfferCache()
    getActiveHeroOffers(salonId).then((result) => {
      if (cancelled) return
      setOffers(result.data || [])
    })
    return () => {
      cancelled = true
    }
  }, [salonReady, salonId])

  const handleWelcomeClick = () => {
    if (typeof onBookNow === 'function') onBookNow()
  }

  const handleOfferClick = (offer) => {
    const slug = slugify(offer.title || 'offer')
    navigate(`/offers/${slug}`)
  }


  const slideCount = 1 + offers.length

  return (
    <section id="home" className="pt-16 sm:pt-20">
      <Swiper
        modules={[Autoplay, Pagination, Navigation]}
        loop={slideCount > 1}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        pagination={{ clickable: true }}
        navigation={true}
        className="heroSwiper"
      >
        <SwiperSlide>
          <div className={`${slideFrameClass} bg-gradient-to-r from-purple-900 to-purple-600`}>
            <OptimizedImage
              src={WELCOME_IMAGE}
              alt="Salon Interior"
              className="w-full h-full object-cover opacity-40"
              loading="eager"
              fetchPriority="high"
              sizes="100vw"
              width={1600}
              height={900}
              decoding="sync"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white px-3 sm:px-4 md:px-6">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 md:mb-6">
                  Welcome to {salonName}
                </h1>
                <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-4 sm:mb-6 md:mb-8 max-w-2xl mx-auto px-2">
                  Experience luxury and relaxation with our premium salon services
                </p>
                <button
                  onClick={handleWelcomeClick}
                  className="bg-secondary text-white px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 rounded-lg text-sm sm:text-base md:text-lg hover:bg-gray-800 transition"
                >
                  Book Now <i className="fas fa-arrow-right ml-2"></i>
                </button>
              </div>
            </div>
          </div>
        </SwiperSlide>

        {offers.map((offer) => (
          <SwiperSlide key={offer.id}>
            <div className={`${slideFrameClass} bg-slate-900`}>
              <OptimizedImage
                src={offer.image_url}
                alt={offer.title}
                className="w-full h-full object-cover"
                loading="lazy"
                sizes="100vw"
                width={1600}
                height={900}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white px-3 sm:px-4 md:px-6 drop-shadow-[0_2px_8px_rgba(0,0,0,0.75)]">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 md:mb-6">
                    {offer.title}
                  </h1>
                  {offer.subtitle ? (
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-4 sm:mb-6 md:mb-8 max-w-2xl mx-auto px-2">
                      {offer.subtitle}
                    </p>
                  ) : null}
                  <button
                    onClick={() => handleOfferClick(offer)}
                    className="bg-secondary text-white px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 rounded-lg text-sm sm:text-base md:text-lg hover:bg-gray-800 transition"
                  >
                    {offer.cta_label} <i className="fas fa-tag ml-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  )
}

export default Hero
